import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {UserHomepageControllerContext, UserHomepageStateContext} from "../../../../index";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Utils from "../../../../../../../core/services/utils/utils";
import {ReactSortable} from "react-sortablejs";
import UserHomepageControlPanelTab from "../tab";
import {UserHomepageControllerEvents, UserHomepageViewEvents} from "../../../../models";
import Api from "../../../../../../../core/services/api/api";
import useCanManageHomepage from "../../../../hooks/use-can-manage-homepage";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import TryAgain from "../../../../../../components/app-specific/try-again";

export const UserHomepageControlPanelTabs = ({onlyRenderComponents}) => {
    const controller = useContext(UserHomepageControllerContext);
    const state = useContext(UserHomepageStateContext);
    const [dragging, setDragging] = useState(false);
    const [reordering, setReordering] = useState(false);
    const isMounted = useIsMounted();
    const canManageHomepage = useCanManageHomepage();
    const {packages} = useAccessLevels();
    const hasHomepageManageFlag =
        packages.clientPlatform.manageWebHomepageEditions ||
        packages.clientPlatform.managePhoneHomepageEditions;

    const loading = useMemo(() => !state?.updatedData, [state?.updatedData])
    /** @type{Array<UserHomepageTab>} */
    const tabs = useMemo(() => state?.updatedData?.tabs ?? [], [state?.updatedData?.tabs])

    /**
     * With each change in updatedTab and controller;
     * - attaches an event lister that would focus the currently selected tab.
     */
    useEffect(() => {
        controller.on(UserHomepageViewEvents.onTabSelected, focusTab);
        controller.on(UserHomepageControllerEvents.onComponentSelected, focusComponent);
        controller.on(UserHomepageViewEvents.onComponentSearchSelected, focusSearchSection);
        controller.on(UserHomepageViewEvents.onContentSelected, focusContent);
        return () => {
            controller.off(UserHomepageViewEvents.onTabSelected, focusTab);
            controller.off(UserHomepageControllerEvents.onComponentSelected, focusComponent);
            controller.off(UserHomepageViewEvents.onComponentSearchSelected, focusSearchSection);
            controller.off(UserHomepageViewEvents.onContentSelected, focusContent);
        }
    }, [controller, tabs])

    /**
     * Focuses the given tab by bringing it into the view.
     * @param {number} tabId
     */
    const focusTab = (tabId) => {
        const tab = tabs?.find(e => e.id === tabId);
        if (!tab)
            return;
        setTimeout(() => {
            document
                .getElementById(tab.controlPanelElementId)
                ?.scrollIntoView(true);
        }, 500)
    }

    /**
     * Focuses the given component by bringing it into the view.
     * @param {UserHomepageEventEmitter} emitter
     * @param {number} tabId
     * @param {number} componentId
     * @param {boolean} scrollToTop
     */
    const focusComponent = (emitter, tabId, componentId, scrollToTop = true) => {
        const tab = tabs?.find(e => e.id === tabId);
        if (!tab)
            return;
        const component = tab?.components?.find(e => e.id === componentId);
        if (!component)
            return;
        setTimeout(() => {
            document
                .getElementById(component.controlPanelElementId)
                ?.scrollIntoView(scrollToTop);
        }, 300)
    }

    /**
     * Focuses the component that the given content belongs to by bringing it into the view.
     * @param {number} tabId
     * @param {number} componentId
     * @param {number} contentId
     */
    const focusContent = (tabId, componentId, contentId) => {
        focusComponent(undefined, tabId, componentId);
    }

    /**
     * Focuses the search section of the component by bringing it into the view.
     * @param {number} tabId
     * @param {number} componentId
     */
    const focusSearchSection = (tabId, componentId) => {
        focusComponent(undefined, tabId, componentId, false);
    }

    /**
     * Persists a same-list tab reorder using Sortable's old/new indices so the
     * moved tab is identified correctly whether it was dragged up or down.
     * @param {import('sortablejs').SortableEvent} event
     */
    const reorderTabs = useCallback(async (event) => {
        if (!canManageHomepage || !hasHomepageManageFlag) return;
        const oldIndex = event.oldDraggableIndex;
        const newIndex = event.newDraggableIndex;
        if (
            typeof oldIndex !== 'number' ||
            typeof newIndex !== 'number' ||
            oldIndex === newIndex ||
            oldIndex < 0 ||
            newIndex < 0 ||
            oldIndex >= tabs.length ||
            newIndex >= tabs.length
        ) {
            return;
        }

        const previousTabs = tabs;
        const movedTab = previousTabs[oldIndex];
        controller.reorderTabs(Utils.reorderList(previousTabs, oldIndex, newIndex));

        setReordering(true);
        const response = await Api.updateHomepageEditionTabOrderIndex({
            editionId: state.id,
            id: movedTab.id,
            newIndex: newIndex + 1,
        });
        if (!isMounted()) return;
        setReordering(false);
        if (!response?.resultFlag) {
            controller.reorderTabs(previousTabs);
        }
    }, [canManageHomepage, controller, hasHomepageManageFlag, isMounted, state.id, tabs]);

    const onDragStart = useCallback(() => setDragging(true), []);
    const onDragEnd = useCallback(() => setDragging(false), []);

    const content = useMemo(() => (
        <ReactSortable
            className={'user-homepage-control-panel-tabs'}
            list={tabs}
            setList={Utils.noop}
            onUpdate={reorderTabs}
            onStart={onDragStart}
            onEnd={onDragEnd}
            handle={'.handle'}
            group={'tabs'}
            animation={210}
            forceFallback={false}
            swapThreshold={0.65}
            ghostClass={"dragged-tab"}
        >
            {tabs.map(tab => (
                <UserHomepageControlPanelTab
                    key={tab.id}
                    tab={tab}
                    sortable={!reordering}
                    dragging={dragging}
                    onlyRenderComponents={onlyRenderComponents}
                />
            ))}
        </ReactSortable>
    ), [dragging, onDragEnd, onDragStart, onlyRenderComponents, reorderTabs, reordering, tabs])

    return (
        <>
            {
                loading &&
                Array(5).fill(null).map((_, e) => (
                    <div key={e} className={'loading control-panel tab'}>
                        <div/>
                    </div>
                ))
            }
            {
                !loading && (
                    !tabs?.length
                        ? <TryAgain
                            text={'This homepage edition does not have any tabs.'}
                        />
                        : content
                )

            }
        </>
    );
}

export default UserHomepageControlPanelTabs;
